/**
 * Coerce a value to a finite number, defaulting to 0.
 *
 * @param {unknown} value
 * @returns {number}
 */
export function toNumber(value) {
  const parsed = Number(value || 0);
  return Number.isFinite(parsed) ? parsed : 0;
}

/**
 * Stored tax rates may be either a fraction (0.07) or a percent (7).
 * Display as a percent number without double-scaling values already > 1.
 *
 * @param {unknown} rate
 * @returns {number}
 */
export function toTaxRatePercent(rate) {
  const n = toNumber(rate);
  if (n === 0) return 0;
  return n > 1 ? n : n * 100;
}

export function toCurrency(value) {
  return Number(toNumber(value).toFixed(2));
}

function createLocalId(prefix = "line") {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

export function createLineItem(sourceType = "manual", taxSettings = {}) {
  return {
    localId: createLocalId(sourceType),
    sourceType,
    sourceId: null,
    serviceDate: null,
    category: sourceType === "job" ? "job" : "manual",
    description: "",
    quantity: 1,
    unitPrice: 0,
    amount: 0,
    taxable: Boolean(taxSettings.defaultTaxable),
    taxCode: taxSettings.defaultTaxCode || "",
    taxRate: toNumber(taxSettings.defaultTaxRate),
    taxAmount: 0,
    metadata: {},
  };
}

export function createDiscount() {
  const localId = createLocalId("discount");

  return {
    localId,
    discountId: localId,
    name: "",
    type: "flat",
    value: 0,
    timing: "preTax",
    applicationOrder: 0,
  };
}

export function createFee() {
  const localId = createLocalId("fee");

  return {
    localId,
    feeId: localId,
    name: "",
    type: "flat",
    value: 0,
    timing: "postTax",
    applicationOrder: 0,
  };
}

export function createCredit() {
  const localId = createLocalId("credit");

  return {
    localId,
    creditId: localId,
    name: "",
    type: "flat",
    value: 0,
    timing: "postTax",
    applicationOrder: 0,
  };
}

export const TERMS_PRESETS = {
  due_on_receipt: { label: "Due on receipt", netDays: 0 },
  net_15: { label: "Net 15", netDays: 15 },
  net_30: { label: "Net 30", netDays: 30 },
  net_45: { label: "Net 45", netDays: 45 },
  net_60: { label: "Net 60", netDays: 60 },
  custom: { label: "Custom", netDays: null },
};

export const TERMS_OPTIONS = [
  { label: "Due on receipt", value: "due_on_receipt" },
  { label: "Net 15", value: "net_15" },
  { label: "Net 30", value: "net_30" },
  { label: "Net 45", value: "net_45" },
  { label: "Net 60", value: "net_60" },
  { label: "Custom", value: "custom" },
];

export const TAX_MODE_OPTIONS = [
  { label: "Line item tax", value: "line_item" },
  { label: "Invoice defaults", value: "invoice_defaults" },
];

export const DEFAULT_TAX_SETTINGS = {
  mode: "line_item",
  defaultTaxable: false,
  defaultTaxRate: 0,
  defaultTaxCode: "",
};

export function createPaymentAllocation() {
  const localId = createLocalId("payment");

  return {
    localId,
    paymentId: localId,
    amount: 0,
    allocatedAt: new Date().toISOString().slice(0, 10),
    method: "manual",
    reference: "",
    note: "",
  };
}

export function getJobDisplayNumber(job) {
  return job?.jobId || job?.loadNumber || job?._id || "";
}

export function createLineItemFromJob(job = {}, taxSettings = {}) {
  const unitPrice = toNumber(job.totalCharges || job.calculatedTotalCharges || 0);
  const jobNumber = getJobDisplayNumber(job);

  return {
    localId: createLocalId("job"),
    sourceType: "job",
    sourceId: job._id || null,
    serviceDate: job.transactionDate || job.appointmentDate || null,
    category: "job",
    description: `Freight movement for job ${jobNumber}`,
    quantity: 1,
    unitPrice,
    amount: unitPrice,
    taxable: Boolean(taxSettings.defaultTaxable),
    taxCode: taxSettings.defaultTaxCode || "",
    taxRate: toNumber(taxSettings.defaultTaxRate),
    taxAmount: 0,
    metadata: {
      jobId: job._id || null,
      loadNumber: job.loadNumber || "",
      jobNumber,
    },
  };
}

export function extractClients(data) {
  return Array.isArray(data?.clients) ? data.clients : [];
}

export function extractJobs(data) {
  if (Array.isArray(data?.jobs)) {
    return data.jobs;
  }

  if (Array.isArray(data?.data?.jobs)) {
    return data.data.jobs;
  }

  if (Array.isArray(data?.data)) {
    return data.data;
  }

  return [];
}

export function createTotalsAdjustments(totals = {}) {
  return {
    creditTotal: toCurrency(totals.creditTotal),
  };
}

export function deriveDueDate(issueDate, netDays) {
  if (!Number.isFinite(Number(netDays))) {
    return "";
  }

  const baseDate = issueDate ? new Date(issueDate) : new Date();
  if (Number.isNaN(baseDate.getTime())) {
    return "";
  }

  baseDate.setUTCDate(baseDate.getUTCDate() + Number(netDays));
  return baseDate.toISOString().slice(0, 10);
}

export function inferTermsCode(terms = "", netDays = null) {
  const numericNetDays = Number(netDays);
  if (Number.isFinite(numericNetDays)) {
    const matchedPreset = Object.entries(TERMS_PRESETS).find(
      ([, preset]) => preset.netDays === numericNetDays
    );
    if (matchedPreset) {
      return matchedPreset[0];
    }
  }

  const normalizedTerms = String(terms || "").trim().toLowerCase();
  switch (normalizedTerms) {
    case "due on receipt":
      return "due_on_receipt";
    case "net 15":
      return "net_15";
    case "net 30":
      return "net_30";
    case "net 45":
      return "net_45";
    case "net 60":
      return "net_60";
    default:
      return "custom";
  }
}

export function buildTermsState({
  terms = "",
  termsCode,
  netDays = null,
  paymentInstructions = "",
  dueDate = "",
  issueDate = "",
} = {}) {
  const safeTermsCode =
    TERMS_PRESETS[termsCode] ? termsCode : inferTermsCode(terms, netDays);
  const preset = TERMS_PRESETS[safeTermsCode] || TERMS_PRESETS.custom;
  const resolvedNetDays =
    safeTermsCode === "custom" ? (Number.isFinite(Number(netDays)) ? Number(netDays) : null) : preset.netDays;
  const resolvedTerms = safeTermsCode === "custom" ? terms || "" : preset.label;
  const resolvedDueDate =
    dueDate || (safeTermsCode === "custom" ? "" : deriveDueDate(issueDate, resolvedNetDays));

  return {
    terms: resolvedTerms,
    termsCode: safeTermsCode,
    netDays: resolvedNetDays,
    paymentInstructions,
    dueDate: resolvedDueDate,
  };
}

export function calculateBalanceDue(grandTotal = 0, paymentAllocations = []) {
  const allocatedAmount = toCurrency(
    (paymentAllocations || []).reduce((sum, item) => sum + toNumber(item.amount), 0)
  );

  return toCurrency(Math.max(0, toNumber(grandTotal) - allocatedAmount));
}

export function deriveInvoiceStatus(status, dueDate, balanceDue, paymentAllocations = []) {
  if (status === "void") {
    return "void";
  }

  if (toNumber(balanceDue) <= 0) {
    return "paid";
  }

  const allocatedAmount = toCurrency(
    (paymentAllocations || []).reduce((sum, item) => sum + toNumber(item.amount), 0)
  );
  const dueDateValue = dueDate ? new Date(dueDate) : null;
  if (
    dueDateValue &&
    !Number.isNaN(dueDateValue.getTime()) &&
    dueDateValue.getTime() < Date.now() &&
    ["issued", "sent", "partially_paid", "overdue"].includes(status)
  ) {
    return "overdue";
  }

  if (allocatedAmount > 0) {
    return "partially_paid";
  }

  return status || "draft";
}

export function formatTaxRatePercent(taxRate) {
  return String(toNumber(taxRate) * 100);
}

export function parseTaxRatePercent(value) {
  return toNumber(value) / 100;
}

export function calculateLineTax(lineItem = {}) {
  if (!lineItem.taxable) {
    return 0;
  }

  const explicitTaxAmount = toNumber(lineItem.taxAmount);
  if (explicitTaxAmount > 0) {
    return toCurrency(explicitTaxAmount);
  }

  return toCurrency(toNumber(lineItem.amount) * toNumber(lineItem.taxRate));
}

function sortEntries(entries = [], idKey = "discountId") {
  return [...entries].sort((left, right) => {
    const orderDelta =
      Number(left.applicationOrder || 0) - Number(right.applicationOrder || 0);

    if (orderDelta !== 0) {
      return orderDelta;
    }

    return String(left[idKey] || left.localId || "").localeCompare(
      String(right[idKey] || right.localId || "")
    );
  });
}

function calculateAdjustmentAmount(baseAmount, adjustment = {}) {
  const safeBaseAmount = toCurrency(baseAmount);
  if (safeBaseAmount <= 0) {
    return 0;
  }

  if (adjustment.type === "percentage") {
    return toCurrency(
      Math.min(safeBaseAmount, safeBaseAmount * (toNumber(adjustment.value) / 100))
    );
  }

  return toCurrency(Math.min(safeBaseAmount, toNumber(adjustment.value)));
}

function distributeAmountAcrossLines(lines = [], totalAppliedAmount = 0, mode = "subtract") {
  const safeAppliedAmount = toCurrency(totalAppliedAmount);
  const totalLineAmount = toCurrency(
    lines.reduce((sum, line) => sum + toNumber(line.runningAmount), 0)
  );

  if (safeAppliedAmount <= 0 || totalLineAmount <= 0 || lines.length === 0) {
    return 0;
  }

  let distributedAmount = 0;

  lines.forEach((line, index) => {
    if (index === lines.length - 1) {
      const remainder = toCurrency(safeAppliedAmount - distributedAmount);
      line.runningAmount = toCurrency(
        mode === "add"
          ? line.runningAmount + remainder
          : Math.max(0, line.runningAmount - remainder)
      );
      distributedAmount = toCurrency(distributedAmount + remainder);
      return;
    }

    const share = toCurrency((line.runningAmount / totalLineAmount) * safeAppliedAmount);
    line.runningAmount = toCurrency(
      mode === "add" ? line.runningAmount + share : Math.max(0, line.runningAmount - share)
    );
    distributedAmount = toCurrency(distributedAmount + share);
  });

  return distributedAmount;
}

export function buildInvoiceV2Financials(
  lineItems = [],
  discounts = [],
  fees = [],
  credits = []
) {
  const workingLines = lineItems.map((lineItem) => ({
    runningAmount: toCurrency(lineItem.amount),
    taxable: Boolean(lineItem.taxable),
    taxRate: toCurrency(lineItem.taxRate),
    taxAmount: toCurrency(lineItem.taxAmount),
  }));

  const sortedDiscounts = sortEntries(discounts, "discountId");
  const sortedFees = sortEntries(fees, "feeId");
  const sortedCredits = sortEntries(credits, "creditId");
  const discountBreakdown = [];
  const feeBreakdown = [];
  const creditBreakdown = [];

  sortedDiscounts
    .filter((discount) => discount.timing === "preTax")
    .forEach((discount) => {
      const baseAmount = toCurrency(
        // Keep service preview rounding aligned with the API totals engine.
        workingLines.reduce((sum, line) => sum + line.runningAmount, 0)
      );
      const appliedAmount = calculateAdjustmentAmount(baseAmount, discount);

      distributeAmountAcrossLines(workingLines, appliedAmount);

      discountBreakdown.push({
        ...discount,
        appliedAmount,
      });
    });

  sortedFees
    .filter((fee) => fee.timing === "preTax")
    .forEach((fee) => {
      const baseAmount = toCurrency(
        workingLines.reduce((sum, line) => sum + line.runningAmount, 0)
      );
      const appliedAmount = calculateAdjustmentAmount(baseAmount, fee);

      distributeAmountAcrossLines(workingLines, appliedAmount, "add");

      feeBreakdown.push({
        ...fee,
        appliedAmount,
      });
    });

  sortedCredits
    .filter((credit) => credit.timing === "preTax")
    .forEach((credit) => {
      const baseAmount = toCurrency(
        workingLines.reduce((sum, line) => sum + line.runningAmount, 0)
      );
      const appliedAmount = calculateAdjustmentAmount(baseAmount, credit);

      distributeAmountAcrossLines(workingLines, appliedAmount);

      creditBreakdown.push({
        ...credit,
        appliedAmount,
      });
    });

  const subtotalAfterPreTaxAdjustments = toCurrency(
    workingLines.reduce((sum, line) => sum + line.runningAmount, 0)
  );
  const taxTotal = toCurrency(
    workingLines.reduce((sum, line) => {
      if (!line.taxable) {
        return sum;
      }

      if (line.taxAmount > 0) {
        return sum + line.taxAmount;
      }

      return sum + toCurrency(line.runningAmount * line.taxRate);
    }, 0)
  );

  let postTaxBase = toCurrency(subtotalAfterPreTaxAdjustments + taxTotal);

  sortedFees
    .filter((fee) => fee.timing === "postTax")
    .forEach((fee) => {
      const appliedAmount = calculateAdjustmentAmount(postTaxBase, fee);
      postTaxBase = toCurrency(postTaxBase + appliedAmount);

      feeBreakdown.push({
        ...fee,
        appliedAmount,
      });
    });

  sortedDiscounts
    .filter((discount) => discount.timing === "postTax")
    .forEach((discount) => {
      const appliedAmount = calculateAdjustmentAmount(postTaxBase, discount);
      postTaxBase = toCurrency(postTaxBase - appliedAmount);

      discountBreakdown.push({
        ...discount,
        appliedAmount,
      });
    });

  sortedCredits
    .filter((credit) => credit.timing === "postTax")
    .forEach((credit) => {
      const appliedAmount = calculateAdjustmentAmount(postTaxBase, credit);
      postTaxBase = toCurrency(postTaxBase - appliedAmount);

      creditBreakdown.push({
        ...credit,
        appliedAmount,
      });
    });

  return {
    taxTotal,
    discountBreakdown,
    feeBreakdown,
    creditBreakdown,
    postTaxBase,
  };
}

export function buildDiscountBreakdown(
  lineItems = [],
  discounts = [],
  fees = [],
  credits = []
) {
  return buildInvoiceV2Financials(lineItems, discounts, fees, credits);
}

export function buildFeeBreakdown(lineItems = [], discounts = [], fees = [], credits = []) {
  return buildInvoiceV2Financials(lineItems, discounts, fees, credits);
}

export function buildCreditBreakdown(
  lineItems = [],
  discounts = [],
  fees = [],
  credits = []
) {
  return buildInvoiceV2Financials(lineItems, discounts, fees, credits);
}

export function buildTotals(
  lineItems,
  adjustments = {},
  discounts = [],
  fees = [],
  credits = [],
  paymentAllocations = []
) {
  const subtotal = toCurrency(
    lineItems.reduce((sum, item) => sum + toNumber(item.amount), 0)
  );
  const {
    taxTotal,
    discountBreakdown,
    feeBreakdown,
    creditBreakdown,
    postTaxBase,
  } = buildInvoiceV2Financials(lineItems, discounts, fees, credits);
  const discountTotal = toCurrency(
    discountBreakdown.reduce((sum, discount) => sum + toNumber(discount.appliedAmount), 0)
  );
  const structuredFeeTotal = toCurrency(
    feeBreakdown.reduce((sum, fee) => sum + toNumber(fee.appliedAmount), 0)
  );
  const legacyFeeTotal =
    Array.isArray(fees) && fees.length > 0 ? 0 : toCurrency(adjustments.feeTotal);
  const feeTotal = toCurrency(structuredFeeTotal + legacyFeeTotal);
  const structuredCreditTotal = toCurrency(
    creditBreakdown.reduce((sum, credit) => sum + toNumber(credit.appliedAmount), 0)
  );
  const legacyCreditTotal =
    Array.isArray(credits) && credits.length > 0
      ? 0
      : toCurrency(adjustments.creditTotal);
  const creditTotal = toCurrency(structuredCreditTotal + legacyCreditTotal);
  const grandTotal = toCurrency(postTaxBase + legacyFeeTotal - legacyCreditTotal);
  const balanceDue = calculateBalanceDue(grandTotal, paymentAllocations);

  return {
    subtotal,
    discountTotal,
    taxTotal,
    feeTotal,
    creditTotal,
    grandTotal,
    balanceDue,
  };
}

export const STATUS_OPTIONS = [
  { label: "Draft", value: "draft" },
  { label: "Review", value: "review" },
  { label: "Issued", value: "issued" },
  { label: "Sent", value: "sent" },
  { label: "Partially paid", value: "partially_paid" },
  { label: "Paid", value: "paid" },
  { label: "Overdue", value: "overdue" },
  { label: "Void", value: "void" },
];

export function getInvoiceStatusLabel(status) {
  return STATUS_OPTIONS.find((option) => option.value === status)?.label || "Unknown";
}

export function getInvoiceStatusColor(status) {
  switch (status) {
    case "draft":
      return "gray";
    case "review":
      return "blue";
    case "issued":
      return "indigo";
    case "sent":
      return "sky";
    case "partially_paid":
      return "amber";
    case "paid":
      return "green";
    case "overdue":
      return "red";
    case "void":
      return "slate";
    default:
      return "gray";
  }
}

export function buildSourceRefs(lineItems, jobs) {
  return lineItems
    .filter((item) => item.sourceType === "job" && item.sourceId)
    .map((item) => {
      const job = jobs.find(
        (jobItem) => String(jobItem._id) === String(item.sourceId)
      );

      return {
        sourceType: item.sourceType,
        sourceId: item.sourceId,
        sourceNumber: job?.jobId || job?.loadNumber || "",
        label: item.description,
      };
    });
}

export function buildInvoiceV2Payload({
  client,
  status,
  terms,
  termsCode,
  netDays,
  paymentInstructions,
  issueDate,
  dueDate,
  taxSettings,
  lineItems,
  discounts,
  fees,
  credits,
  paymentAllocations,
  totals,
  tags,
}) {
  const normalizedLineItems = lineItems.map((lineItem) => ({
    sourceType: lineItem.sourceType,
    sourceId: lineItem.sourceId || null,
    serviceDate: lineItem.serviceDate || null,
    category: lineItem.category,
    description: lineItem.description,
    quantity: toCurrency(lineItem.quantity),
    unitPrice: toCurrency(lineItem.unitPrice),
    amount: toCurrency(lineItem.amount),
    taxable: Boolean(lineItem.taxable),
    taxCode: lineItem.taxCode || null,
    taxRate: toCurrency(lineItem.taxRate),
    taxAmount: lineItem.taxable
      ? toCurrency(lineItem.taxAmount) ||
        toCurrency(toNumber(lineItem.amount) * toNumber(lineItem.taxRate))
      : 0,
    metadata: lineItem.metadata || {},
  }));
  const normalizedDiscounts = sortEntries(discounts || [], "discountId").map((discount, index) => ({
    discountId: discount.discountId || discount.localId || `discount_${index + 1}`,
    name: String(discount.name || "").trim(),
    type: discount.type || "flat",
    value: toCurrency(discount.value),
    timing: discount.timing || "preTax",
    applicationOrder: Number.isFinite(Number(discount.applicationOrder))
      ? Number(discount.applicationOrder)
      : index,
  }));
  const normalizedFees = sortEntries(fees || [], "feeId").map((fee, index) => ({
    feeId: fee.feeId || fee.localId || `fee_${index + 1}`,
    name: String(fee.name || "").trim(),
    type: fee.type || "flat",
    value: toCurrency(fee.value),
    timing: fee.timing || "postTax",
    applicationOrder: Number.isFinite(Number(fee.applicationOrder))
      ? Number(fee.applicationOrder)
      : index,
  }));
  const normalizedCredits = sortEntries(credits || [], "creditId").map((credit, index) => ({
    creditId: credit.creditId || credit.localId || `credit_${index + 1}`,
    name: String(credit.name || "").trim(),
    type: credit.type || "flat",
    value: toCurrency(credit.value),
    timing: credit.timing || "postTax",
    applicationOrder: Number.isFinite(Number(credit.applicationOrder))
      ? Number(credit.applicationOrder)
      : index,
  }));
  const normalizedPaymentAllocations = (paymentAllocations || []).map((allocation, index) => ({
    paymentId: allocation.paymentId || allocation.localId || `payment_${index + 1}`,
    amount: toCurrency(allocation.amount),
    allocatedAt: allocation.allocatedAt || null,
    method: allocation.method || "manual",
    reference: allocation.reference || "",
    note: allocation.note || "",
  }));
  const normalizedTaxSettings = {
    ...DEFAULT_TAX_SETTINGS,
    ...(taxSettings || {}),
    defaultTaxRate: toCurrency(taxSettings?.defaultTaxRate),
    defaultTaxable: Boolean(taxSettings?.defaultTaxable),
    defaultTaxCode: taxSettings?.defaultTaxCode || "",
  };

  const payload = {
    client: client?._id || client,
    status,
    terms,
    termsCode: termsCode || inferTermsCode(terms, netDays),
    netDays: Number.isFinite(Number(netDays)) ? Number(netDays) : null,
    paymentInstructions: paymentInstructions || "",
    issueDate: issueDate || null,
    dueDate: dueDate || null,
    currency: "USD",
    taxSettings: normalizedTaxSettings,
    discounts: normalizedDiscounts,
    fees: normalizedFees,
    credits: normalizedCredits,
    paymentAllocations: normalizedPaymentAllocations,
    lineItems: normalizedLineItems,
    totals:
      totals ||
      buildTotals(
        lineItems,
        {},
        normalizedDiscounts,
        normalizedFees,
        normalizedCredits,
        normalizedPaymentAllocations
      ),
  };

  if (tags !== undefined) {
    payload.tags = tags;
  }

  return payload;
}

export function hydrateDiscounts(invoice) {
  return (invoice?.discounts || []).map((discount, index) => ({
    localId: discount.discountId || `discount_${index + 1}`,
    discountId: discount.discountId || `discount_${index + 1}`,
    name: discount.name || "",
    type: discount.type || "flat",
    value: discount.value ?? 0,
    timing: discount.timing || "preTax",
    applicationOrder:
      Number.isFinite(Number(discount.applicationOrder)) ? discount.applicationOrder : index,
    appliedAmount: discount.appliedAmount ?? 0,
  }));
}

export function hydrateFees(invoice) {
  if (Array.isArray(invoice?.fees) && invoice.fees.length > 0) {
    return invoice.fees.map((fee, index) => ({
      localId: fee.feeId || `fee_${index + 1}`,
      feeId: fee.feeId || `fee_${index + 1}`,
      name: fee.name || "",
      type: fee.type || "flat",
      value: fee.value ?? 0,
      timing: fee.timing || "postTax",
      applicationOrder:
        Number.isFinite(Number(fee.applicationOrder)) ? fee.applicationOrder : index,
      appliedAmount: fee.appliedAmount ?? 0,
    }));
  }

  const legacyFeeTotal = toCurrency(invoice?.totals?.feeTotal);
  if (legacyFeeTotal <= 0) {
    return [];
  }

  return [
    {
      localId: "fee_1",
      feeId: "fee_1",
      name: "Fee",
      type: "flat",
      value: legacyFeeTotal,
      timing: "postTax",
      applicationOrder: 0,
      appliedAmount: legacyFeeTotal,
    },
  ];
}

export function hydrateCredits(invoice) {
  if (Array.isArray(invoice?.credits) && invoice.credits.length > 0) {
    return invoice.credits.map((credit, index) => ({
      localId: credit.creditId || `credit_${index + 1}`,
      creditId: credit.creditId || `credit_${index + 1}`,
      name: credit.name || "",
      type: credit.type || "flat",
      value: credit.value ?? 0,
      timing: credit.timing || "postTax",
      applicationOrder:
        Number.isFinite(Number(credit.applicationOrder)) ? credit.applicationOrder : index,
      appliedAmount: credit.appliedAmount ?? 0,
    }));
  }

  const legacyCreditTotal = toCurrency(invoice?.totals?.creditTotal);
  if (legacyCreditTotal <= 0) {
    return [];
  }

  return [
    {
      localId: "credit_1",
      creditId: "credit_1",
      name: "Credit",
      type: "flat",
      value: legacyCreditTotal,
      timing: "postTax",
      applicationOrder: 0,
      appliedAmount: legacyCreditTotal,
    },
  ];
}

export function hydrateTaxSettings(invoice) {
  return {
    ...DEFAULT_TAX_SETTINGS,
    ...(invoice?.taxSettings || {}),
    defaultTaxRate: toNumber(invoice?.taxSettings?.defaultTaxRate),
    defaultTaxable: Boolean(invoice?.taxSettings?.defaultTaxable),
    defaultTaxCode: invoice?.taxSettings?.defaultTaxCode || "",
  };
}

export function hydratePaymentAllocations(invoice) {
  return (invoice?.paymentAllocations || []).map((allocation, index) => ({
    localId: allocation.paymentId || `payment_${index + 1}`,
    paymentId: allocation.paymentId || `payment_${index + 1}`,
    amount: allocation.amount ?? 0,
    allocatedAt: allocation.allocatedAt
      ? String(allocation.allocatedAt).slice(0, 10)
      : "",
    method: allocation.method || "manual",
    reference: allocation.reference || "",
    note: allocation.note || "",
  }));
}

export function hydrateTerms(invoice) {
  return buildTermsState({
    terms: invoice?.terms || "",
    termsCode: invoice?.termsCode,
    netDays: invoice?.netDays,
    paymentInstructions: invoice?.paymentInstructions || "",
    dueDate: invoice?.dueDate ? String(invoice.dueDate).slice(0, 10) : "",
    issueDate: invoice?.issueDate ? String(invoice.issueDate).slice(0, 10) : "",
  });
}

export function hydrateLineItems(invoice) {
  return (invoice?.lineItems || []).map((lineItem, index) => ({
    localId: lineItem.lineId || `line_${index + 1}`,
    sourceType: lineItem.sourceType || "manual",
    sourceId: lineItem.sourceId || null,
    serviceDate: lineItem.serviceDate || null,
    category: lineItem.category || "manual",
    description: lineItem.description || "",
    quantity: lineItem.quantity ?? 1,
    unitPrice: lineItem.unitPrice ?? 0,
    amount: lineItem.amount ?? 0,
    taxable: Boolean(lineItem.taxable),
    taxCode: lineItem.taxCode || "",
    taxRate: lineItem.taxRate ?? 0,
    taxAmount: lineItem.taxAmount ?? 0,
    metadata: lineItem.metadata || {},
  }));
}
